import { normalize, schema } from 'normalizr';
import { paginationFactory } from 'redux/reusable/pagination';
import { xhrStatusFactory } from 'redux/reusable/xhrStatus';
import type { RawInvoice } from 'types/invoice';
import { InvoiceBy, InvoiceDates, InvoiceMethods, ItemGrouping } from 'types/invoice';
import { reducerWithInitialState } from 'typescript-fsa-reducers';
import type { State } from './invoice.types';
import {
  clearInvoiceDraft,
  clearInvoices,
  clearPagination,
  clearQuery,
  createdInvoice,
  createInvoiceRulesDraft,
  deletedInvoice,
  fetchedInvoice,
  fetchedInvoiceRules,
  fetchedInvoices,
  INVOICE_PAGE_LIMIT,
  InvoiceListFields,
  invoiceXhrModule,
  setInvoiceDraft,
  setInvoicePreview,
  setQuery,
  setSortBy,
  toggleCurrentInvoiceId,
  updateInvoiceDraft,
  updateInvoiceRulesDraft,
  withPaginationReducer,
} from './invoice.types';

const invoiceSchema = new schema.Entity('invoice');
const invoiceArraySchema = new schema.Array(invoiceSchema);

interface InvoiceNormalized {
  entities: {
    invoice: {
      [id: number]: RawInvoice;
    };
  };
  result: Array<number>;
}

const { withXhrStatusReducer } = invoiceXhrModule;

export const initialState: State = {
  entities: {},
  result: [],
  rules: null,
  draft: null,
  currentInvoiceId: null,
  preview: null,
  rulesDraft: {
    id: 0,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    invoice_method: InvoiceMethods.Automatic,
    standard_invoice_date: InvoiceDates.FirstOfMonth,
    invoice_by: InvoiceBy.Project,
    list_item_grouping: ItemGrouping.Projects,
  },
  pagination: paginationFactory({ limit: INVOICE_PAGE_LIMIT, order: 'desc' }),
  sortBy: {
    field: InvoiceListFields.UpdatedAt,
    direction: 'desc',
  },
  query: {
    show: 'active',
    status: 'all',
    projectIds: [],
    userIds: [],
  },
  xhrStatus: xhrStatusFactory(),
};

const reducer = reducerWithInitialState<State>(initialState)
  .caseWithAction(fetchedInvoices, (state, action) => {
    const { entities, result }: InvoiceNormalized = normalize(
      action.payload.invoices,
      invoiceArraySchema
    );
    if (action.payload.freshState) {
      return {
        ...state,
        entities: entities.invoice,
        result,
      };
    }
    return {
      ...state,
      entities: { ...state.entities, ...entities.invoice },
      result: [...new Set([...state.result, ...result])],
    };
  })
  .case(clearInvoices, state => {
    return {
      ...state,
      entities: initialState.entities,
      result: initialState.result,
    };
  })
  .caseWithAction(fetchedInvoice, (state, action) => {
    const invoiceById = state.entities[action.payload.id] || {};
    return {
      ...state,
      entities: {
        ...state.entities,
        [action.payload.id]: { ...invoiceById, isAwaitingSync: false, ...action.payload },
      },
      result: [...new Set([...state.result, action.payload.id])],
    };
  })
  .caseWithAction(createdInvoice, (state, action) => ({
    ...state,
    entities: {
      ...state.entities,
      [action.payload.id]: action.payload,
    },
    result: [...new Set([...state.result, action.payload.id])],
  }))
  .caseWithAction(deletedInvoice, (state, action) => {
    const { [action.payload.id]: remove, ...remainingInvoices } = state.entities;
    return {
      ...state,
      entities: remainingInvoices,
      result: state.result.filter(id => id !== action.payload.id),
    };
  })
  .case(clearInvoices, state => {
    return {
      ...state,
      entities: {},
      result: [],
    };
  })
  .caseWithAction(fetchedInvoiceRules, (state, action) => ({
    ...state,
    rules: action.payload,
  }))
  .caseWithAction(setInvoicePreview, (state, action) => ({
    ...state,
    preview: action.payload,
  }))
  .caseWithAction(updateInvoiceRulesDraft, (state, action) => ({
    ...state,
    rulesDraft: { ...state.rulesDraft, ...action.payload },
  }))
  .caseWithAction(createInvoiceRulesDraft, (state, action) => ({
    ...state,
    rulesDraft: { ...state.rulesDraft, ...action.payload },
  }))
  .caseWithAction(setInvoiceDraft, (state, action) => {
    if (state.draft) {
      return {
        ...state,
        draft: { ...state.draft, ...action.payload },
      };
    }
    return {
      ...state,
      draft: action.payload,
    };
  })
  .caseWithAction(updateInvoiceDraft, (state, action) => {
    if (state.draft) {
      return {
        ...state,
        draft: { ...state.draft, ...action.payload },
      };
    }
    return state;
  })
  .case(clearInvoiceDraft, state => ({
    ...state,
    draft: null,
  }))
  .caseWithAction(setQuery, (state, action) => ({
    ...state,
    query: { ...state.query, ...action.payload },
  }))
  .case(clearQuery, state => ({
    ...state,
    query: initialState.query,
  }))
  .case(clearPagination, state => ({
    ...state,
    pagination: initialState.pagination,
  }))
  .caseWithAction(toggleCurrentInvoiceId, (state, action) => ({
    ...state,
    currentInvoiceId: action.payload.id,
  }))
  .caseWithAction(setSortBy, (state, action) => ({
    ...state,
    sortBy: {
      field: action.payload.field,
      direction: action.payload.direction,
    },
  }));

export default withXhrStatusReducer(withPaginationReducer(reducer));
